import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { propType } from 'graphql-anywhere'
import classNames from 'classnames'
import { useQuery } from 'react-apollo'
import { Helmet } from 'react-helmet'
import {
  pathOr, head, isEmpty
} from 'ramda'

import DetailRateButtonAndModal from '../../modules/user-rating/detail-rate-modal'
import HeroImage from '../shared/hero-image'
import ShowPageHeading from '../shared/show-page-heading'
import ShowPageStatus from '../shared/show-page-status'
import { WatchButton, ReplayButton } from '../../containers/play-button/showpage-play-button'
import AddOrRemoveFromListButton from '../showpage-buttons/add-or-remove-from-list-button'
import Quote from '../shared/quote'
import styles from './title-detail.css'
import {
  getFirstTrailer,
  getEnableAutoplayTrailer,
  getDetailsFullUrl,
  getTwoGenres
} from '../../lib/utils'
import { getLowestPrice, getRentalStatus } from '../../lib/content-item'
import { getDurationTextFromMinutes } from '../../lib/date-time'
import RentalInfo from '../shared/rental-info'
import AutoplayTrailerStream from '../autoplay-trailer/autoplay-trailer-stream'
import CONTENT_ITEM_FRAGMENT from '../../../graphql/fragments/content-item.gql'
import { getLocationBaseWithHref } from '../../lib/location'
import { openGraphMetaImages } from '../../lib/open-graph/images'
import { openGraphMetaActors } from '../../lib/open-graph/actors'
import { openGraphMetaDirectors } from '../../lib/open-graph/directors'
import { openGraphMetaTags } from '../../lib/open-graph/tags'
import { PLAY_BUTTON_IN_TITLE_HERO } from '../../containers/play-button/constants'
import { ViewTrailerButton } from '../../containers/shared/trailers/trailer'
import { RENTAL_STATUS, SCREEN_SIZE_BREAKPOINTS } from '../../constants'
import rentalBadge from '../../../images/rental-badge.svg'
import {
  COMPONENT_NAME_TITLE_DETAIL
} from '../../lib/analytics/custom-dimensions'
import { E2E_SELECTORS } from '../../e2e-selectors'
import RentalNotes from '../shared/rental-notes'
import PROFILE_PREFERENCES_QUERY from '../../../graphql/queries/profile-preferences.gql'
import RENTAL_PAYMENT_VIEW_QUERY from '../../../graphql/queries/rental-payment-view.gql'
import MY_RENTALS_QUERY from '../../../graphql/queries/my-rentals.gql'
import { useIsAuthenticated } from '../../hooks/use-is-authenticated'
import useInitializeCastSender from '../../hooks/use-initialize-cast-sender'
import useTrackShowPageSideEffects from '../../hooks/useTrackShowPageSideEffects'

function TitleDetail({
  contentItem,
  onNotesLoaded,
  margin,
  screenWidth
}) {
  const contentItemId = contentItem?.id
  const products = pathOr([], ['products'], contentItem)
  const selectedProduct = head(products)
  const selectedProductId = selectedProduct?.id
  const isRental = pathOr(false, ['isRental'], contentItem)
  const isAuthenticated = useIsAuthenticated()
  const profilePreferencesQuery = useQuery(PROFILE_PREFERENCES_QUERY, {
    fetchPolicy: 'cache-first',
    skip: !isAuthenticated
  })
  const trailerAutoplayOnShowPageEnabled = profilePreferencesQuery?.data
    ?.profilePreferences?.trailerAutoplayOnShowPageEnabled

  useTrackShowPageSideEffects({
    contentItem,
    componentName: COMPONENT_NAME_TITLE_DETAIL
  })

  const { data: rentalPaymentViewData } = useQuery(RENTAL_PAYMENT_VIEW_QUERY, {
    variables: {
      contentItemId,
      productId: selectedProductId
    },
    fetchPolicy: 'network-only',
    skip: !isAuthenticated || !contentItemId || isEmpty(products) || !isRental
  })
  const timeToBeginWatch = pathOr(
    null,
    ['rentalPaymentView', 'timeToBeginWatch'],
    rentalPaymentViewData
  )
  const hoursFromFirstPlay = pathOr(
    null,
    ['rentalPaymentView', 'hoursFromFirstPlay'],
    rentalPaymentViewData
  )
  const freeWithSubscription = pathOr(
    null,
    ['rentalPaymentView', 'freeWithSubscription'],
    rentalPaymentViewData
  )
  const svodMoviePath = pathOr(
    null,
    ['rentalPaymentView', 'svodMoviePath'],
    rentalPaymentViewData
  )
  const {
    data: myRentalsData,
    loading: myRentalsLoading
  } = useQuery(MY_RENTALS_QUERY, {
    variables: {
      id: contentItemId
    },
    fetchPolicy: 'network-only',
    skip: !isAuthenticated || !contentItemId
  })

  useEffect(() => {
    onNotesLoaded({
      timeToBeginWatch,
      hoursFromFirstPlay
    })
  }, [hoursFromFirstPlay, onNotesLoaded, timeToBeginWatch])

  const { castPreviewPageSender } = useInitializeCastSender()
  useEffect(() => {
    castPreviewPageSender(contentItem)
  }, [castPreviewPageSender, contentItem])

  if (!contentItem || myRentalsLoading) {
    return null
  }

  const rentalStatus = getRentalStatus(contentItem, myRentalsData?.myRentalHistory)
  const truncatedGenres = getTwoGenres(contentItem)
  const year = contentItem.year ?? null
  const duration = contentItem.duration
    ? getDurationTextFromMinutes(contentItem.duration)
    : null
  const badges = [...truncatedGenres, year, duration]
  const quote = contentItem.quotes?.[0] ?? null
  const lowestPrice = getLowestPrice(contentItem)
  const imageUri = contentItem.keyart?.uri
  const enableAutoplayTrailers = getEnableAutoplayTrailer(contentItem)
  const trailer = getFirstTrailer(enableAutoplayTrailers)
  const displayRentalInfo =
    rentalStatus === RENTAL_STATUS.RENTED && contentItem.rentalInfo != null
  const brand = 'NEON'
  const fromPrice = isRental && lowestPrice ? ` $${lowestPrice}` : ''
  const title = `Watch ${contentItem.title} Online with ${brand}${fromPrice}`
  const showSquareHeroImage = screenWidth <= SCREEN_SIZE_BREAKPOINTS.MIN_480
  const autoplayTrailerEnabled = trailerAutoplayOnShowPageEnabled && trailer

  return (
    <>
      <div className={styles.titleDetail}>
        <Helmet>
          <meta property="og:title" content={title} />
          <meta property="og:type" content="video.movie" />
          <meta property="og:url" content={getLocationBaseWithHref()} />
          <meta property="og:video:release_date" content={contentItem.year} />
          <meta
            property="og:video:duration"
            content={contentItem.duration * 60}
          />
          {openGraphMetaImages(contentItem.images)}
          {openGraphMetaActors(contentItem.actors)}
          {openGraphMetaDirectors(contentItem.directors)}
          {openGraphMetaTags(contentItem.genres)}
          <meta name="description" property="og:description" content={title} />
          <link rel="canonical" href={getDetailsFullUrl(contentItem)} />
          <title>{title}</title>
        </Helmet>

        {/* show autoplay trailer when user is logged in and trailer autoplay is enabled */}
        {autoplayTrailerEnabled ? (
          <AutoplayTrailerStream content={trailer} image={imageUri} />
        ) : (
          <HeroImage
            image={imageUri}
            title={contentItem.title}
            brandLogo={contentItem.brandOrDistributorLogo}
            showSquareHeroImage={showSquareHeroImage}
          />
        )}

        {isRental && (
          <img
            src={rentalBadge}
            alt="Rental Content"
            className={styles.rentalBadge}
          />
        )}

        <div className={classNames(styles.header, {
          [styles.headerUnderTrailer]: autoplayTrailerEnabled
        })}
        >
          <div
            className={styles.leftHeader}
            style={{
              marginLeft: margin
            }}
          >
            <ShowPageHeading
              title={contentItem.title}
              titleTreatment={contentItem.titleTreatment}
              brandLogo={contentItem.brandOrDistributorLogo}
              e2eIdentifier={E2E_SELECTORS.MOVIE_TITLE}
            />

            <ShowPageStatus
              contentItem={contentItem}
              badges={badges}
            />

            {displayRentalInfo && (
              <RentalInfo rentalInfo={contentItem.rentalInfo} />
            )}

            <div className={styles.buttonsWrapper}>
              <WatchButton
                displayedInLocation={PLAY_BUTTON_IN_TITLE_HERO}
                contentItem={contentItem}
                rentalNote={freeWithSubscription}
                svodMoviePath={svodMoviePath}
              />

              <ReplayButton
                displayedInLocation={PLAY_BUTTON_IN_TITLE_HERO}
                contentItem={contentItem}
              />

              {trailer && (
                <ViewTrailerButton
                  trailer={trailer}
                  contentItem={contentItem}
                  id={trailer.id}
                  renderedInComponent={COMPONENT_NAME_TITLE_DETAIL}
                />
              )}

              <AddOrRemoveFromListButton
                contentItem={contentItem}
                renderedInComponent={COMPONENT_NAME_TITLE_DETAIL}
              />

              <DetailRateButtonAndModal
                contentItem={contentItem}
                isAuthenticated={isAuthenticated}
              />
            </div>

            {(timeToBeginWatch || hoursFromFirstPlay) && (
              <div className={styles.rentalNotesFrame}>
                <RentalNotes
                  timeToBeginWatch={timeToBeginWatch}
                  hoursFromFirstPlay={hoursFromFirstPlay}
                />
              </div>
            )}
          </div>

          <div className={styles.rightHeader}>
            {quote && (
              <Quote text={quote.quote} author={quote.attribution} />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

TitleDetail.propTypes = {
  contentItem: propType(CONTENT_ITEM_FRAGMENT).isRequired,
  onNotesLoaded: PropTypes.func.isRequired,
  margin: PropTypes.number.isRequired,
  screenWidth: PropTypes.number.isRequired
}

export default TitleDetail
